import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailField } from '@/components/ui/entity-detail-field';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import type { LimitPolicyQuery } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { FileText, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { LIMIT_POLICY_QUERY } from './limit-policies.gql.tsx';

type LimitPolicyDetailsData = NonNullable<LimitPolicyQuery['limitPolicy']>;

interface LimitPolicyDetailsProps {
	limitPolicyId: string;
}

export function LimitPolicyDetails({ limitPolicyId }: LimitPolicyDetailsProps) {
	const { t } = useTranslation('common');
	const { t: tLP } = useTranslation('limitPolicies');
	const { data, isLoading, isError } = useOne<LimitPolicyDetailsData>({
		resource: 'limitPolicies',
		id: limitPolicyId,
		meta: {
			gqlQuery: LIMIT_POLICY_QUERY,
			dataPath: 'limitPolicy'
		}
	});

	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 100,
		showInitialLoading: true
	});

	const limitPolicy = data?.data;

	if (shouldShowLoading) {
		return (
			<div className="space-y-6">
				<div className="space-y-2">
					<div className="w-40 h-6 rounded bg-muted animate-pulse" />
					<div className="w-32 h-4 rounded bg-muted animate-pulse" />
					<div className="w-24 h-4 rounded bg-muted animate-pulse" />
				</div>
				<div className="h-32 rounded-md bg-muted animate-pulse" />
				<div className="h-32 rounded-md bg-muted animate-pulse" />
				<div className="h-32 rounded-md bg-muted animate-pulse" />
			</div>
		);
	}

	if (!isLoading && (isError || !limitPolicy)) {
		return (
			<div className="py-8 text-sm text-center text-muted-foreground">
				{tLP('details.loadError')}
			</div>
		);
	}

	if (!limitPolicy) {
		return null;
	}

	return (
		<Tabs defaultValue="details" className="w-full">
			<TabsList className="gap-2 bg-transparent p-0">
				<TabsTrigger value="details" className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold">
					<FileText className="w-4 h-4" />
					<span>{t('tabs.details')}</span>
				</TabsTrigger>
				<TabsTrigger value="audit" align="right" className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold">
					<History className="w-4 h-4" />
					<span>{t('tabs.audit')}</span>
				</TabsTrigger>
			</TabsList>

			<TabsContent value="details" className="space-y-6 text-base mt-6">
				<DetailSection>
					<DetailFieldsGrid>
						<DetailField
							label={tLP('details.priority')}
							value={
								limitPolicy.priority?.toString() ??
								tLP('details.notSet')
							}
						/>
						<DetailField
							label={tLP('details.billingCycle')}
							value={
								limitPolicy.billingCycle?.name ??
								tLP('details.notSet')
							}
						/>
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={t('sections.scenario_assignment')}>
					<DetailFieldsGrid>
						<EntityDetailField
							label={tLP('details.scenario')}
							value={limitPolicy.scenario?.name}
							entityId={limitPolicy.scenario?.scenarioId}
							entityType="scenario"
						/>
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={t('sections.limit_enforcement')}>
					<DetailFieldsGrid>
						<EntityDetailField
							label={tLP('details.limit')}
							value={limitPolicy.limit?.name}
							entityId={limitPolicy.limit?.limitId}
							entityType="limit"
						/>
					</DetailFieldsGrid>
				</DetailSection>
			</TabsContent>

			<TabsContent value="audit" className="mt-6">
				<AuditTab
					data={{
						activatedAt: limitPolicy.activatedAt,
						deactivatedAt: limitPolicy.deactivatedAt
					}}
				/>
			</TabsContent>
		</Tabs>
	);
}
